import React from "react";

const SOURCE_TABS = [
  { id: "all", label: "All", icon: "fa-th-large" },
  { id: "streaming", label: "Streaming", icon: "fa-music" },
  { id: "youtube", label: "YouTube", icon: "fa-youtube-play" },
];

function formatRevenue(total, currency) {
  if (!total) return "—";
  const formatted = Number(total).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

export default function IncomeListFilters({
  summary,
  searchTerm,
  onSearchChange,
  yearFilter,
  onYearChange,
  availableYears,
  sourceFilter,
  onSourceChange,
  onReset,
  hasActiveFilters,
  onSyncYoutube,
  onViewYoutubeUrls,
}) {
  const isYoutubeView = sourceFilter === "youtube";
  const isStreamingView = sourceFilter === "streaming";

  return (
    <div className="inc-filter-panel">
      <div className="inc-filter-panel__tabs-row">
        <div className="inc-source-tabs" role="tablist" aria-label="Income source">
          {SOURCE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={sourceFilter === tab.id}
              className={`inc-source-tab ${sourceFilter === tab.id ? "inc-source-tab--active" : ""} ${
                tab.id === "youtube" ? "inc-source-tab--youtube" : ""
              }`}
              onClick={() => onSourceChange(tab.id)}
            >
              <i className={`fa ${tab.icon} me-1`} aria-hidden="true" />
              {tab.label}
              {tab.id === "youtube" && summary.youtube > 0 ? (
                <span className="inc-source-tab__count">{summary.youtube}</span>
              ) : null}
              {tab.id === "streaming" && summary.streaming > 0 ? (
                <span className="inc-source-tab__count">{summary.streaming}</span>
              ) : null}
            </button>
          ))}
        </div>

        {isYoutubeView ? (
          <div className="inc-youtube-quick-actions">
            <button type="button" className="inc-youtube-quick-actions__btn" onClick={onSyncYoutube}>
              <i className="fa fa-refresh" aria-hidden="true" />
              Sync
            </button>
            <button type="button" className="inc-youtube-quick-actions__btn" onClick={onViewYoutubeUrls}>
              <i className="fa fa-link" aria-hidden="true" />
              URLs
            </button>
          </div>
        ) : null}
      </div>

      {isYoutubeView ? (
        <div className="inc-filter-panel__hint inc-filter-panel__hint--youtube">
          <i className="fa fa-info-circle" aria-hidden="true" />
          YouTube income is grouped by reporting period (FUGA-style) with the total for the
          platform. Expand a period to see each artist, their revenue and invoicing.
        </div>
      ) : null}

      <div className="inc-filter-panel__metrics">
        {isYoutubeView ? (
          <>
            <div className="inc-metric inc-metric--youtube">
              <span className="inc-metric__value">{summary.youtube}</span>
              <span className="inc-metric__label">Periods</span>
            </div>
            <div className="inc-metric">
              <span className="inc-metric__value">{summary.youtubeArtists ?? 0}</span>
              <span className="inc-metric__label">Artists</span>
            </div>
            <div className="inc-metric inc-metric--youtube-revenue">
              <span className="inc-metric__value inc-metric__value--sm">
                {formatRevenue(summary.youtubeRevenue, summary.youtubeCurrency)}
              </span>
              <span className="inc-metric__label">Est. revenue</span>
            </div>
            <div className="inc-metric inc-metric--success">
              <span className="inc-metric__value">{summary.youtubeDone ?? 0}</span>
              <span className="inc-metric__label">Synced</span>
            </div>
          </>
        ) : isStreamingView ? (
          <>
            <div className="inc-metric">
              <span className="inc-metric__value">{summary.streaming}</span>
              <span className="inc-metric__label">Imports</span>
            </div>
            <div className="inc-metric inc-metric--success">
              <span className="inc-metric__value">{summary.streamingDone ?? 0}</span>
              <span className="inc-metric__label">Processed</span>
            </div>
            <div className="inc-metric inc-metric--warn">
              <span className="inc-metric__value">{summary.streamingPending ?? 0}</span>
              <span className="inc-metric__label">Pending</span>
            </div>
          </>
        ) : (
          <>
            <div className="inc-metric">
              <span className="inc-metric__value">{summary.total}</span>
              <span className="inc-metric__label">Total</span>
            </div>
            <div className="inc-metric">
              <span className="inc-metric__value">{summary.streaming}</span>
              <span className="inc-metric__label">Streaming</span>
            </div>
            <div className="inc-metric inc-metric--youtube">
              <span className="inc-metric__value">{summary.youtube}</span>
              <span className="inc-metric__label">YouTube</span>
            </div>
            <div className="inc-metric inc-metric--success">
              <span className="inc-metric__value">{summary.done}</span>
              <span className="inc-metric__label">Done</span>
            </div>
            <div className="inc-metric inc-metric--warn">
              <span className="inc-metric__value">{summary.pending}</span>
              <span className="inc-metric__label">Pending</span>
            </div>
          </>
        )}
      </div>

      <div className="inc-filter-panel__bar">
        <label className="inc-search">
          <i className="fa fa-search inc-search__icon" aria-hidden="true" />
          <input
            type="search"
            className="inc-search__input"
            placeholder={
              isYoutubeView ? "Search periods or artists…" : "Search incomes…"
            }
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search incomes"
          />
        </label>

        <div className="inc-filter-panel__selects">
          <label className="inc-select-field">
            <span className="inc-select-field__label">Year</span>
            <select
              className="form-select form-select-sm inc-select-field__control"
              value={yearFilter}
              onChange={(e) => onYearChange(e.target.value)}
              aria-label="Filter by year"
            >
              <option value="all">All years</option>
              {availableYears.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>

        {hasActiveFilters ? (
          <button type="button" className="inc-filter-panel__reset" onClick={onReset}>
            <i className="fa fa-times" aria-hidden="true" />
            Clear filters
          </button>
        ) : null}
      </div>
    </div>
  );
}
